// src/app/modules/apps/call/wallboard/WallboardPage.tsx
import React, { useState, useRef, useEffect, useCallback, FC } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import { useThemeMode } from '../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {
  RefreshCw, Clock, Target, Star, Phone, PhoneMissed,
  CheckCircle, AlertCircle, Users, Activity, Zap,
  BarChart3, Timer, Headphones, MessageSquare, Ticket,
  TrendingUp, TrendingDown, Award, Wifi, WifiOff,
  UserCheck, UserX, Gauge, Radio, Signal,
  BarChart2, PieChart as PieChartIcon, Activity as ActivityIcon,
  GripVertical, Move, LayoutGrid, Columns, Rows, Calendar,
  FolderOpen, CheckSquare, UserPlus, AlertTriangle
} from 'lucide-react'
import { RealtimeCounts } from './RealtimeCounts'
import ServiceLevelInbound from './ServiceLevelInbound'
import AnalyticsPieCard from './AnalyticsPieCard'
import SatisfactionBarChart from './SatisfactionBarChart'

import './Wallboard.css'

// ✅ IMPORT DATE RANGE PICKER
import DateRangePicker from 'react-bootstrap-daterangepicker'
import 'bootstrap-daterangepicker/daterangepicker.css'
import moment, { Moment } from 'moment'

const fmt = (n: number) => new Intl.NumberFormat().format(n)

// ✅ FORMAT DATE
const formatDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// ==================== HORIZONTAL BAR CHART COMPONENT ====================
interface BarChartItem {
  label: string
  value: number
  color?: string
}

interface HorizontalBarChartProps {
  data: BarChartItem[]
  title: string
  icon: any
  color: string
  maxValue?: number
  isDark: boolean
  dateRange?: { start: Date; end: Date }
}

const HorizontalBarChart: FC<HorizontalBarChartProps> = ({
  data,
  title,
  icon: Icon,
  color,
  maxValue,
  isDark,
  dateRange
}) => {
  const max = maxValue || Math.max(...data.map(d => d.value))

  return (
    <div style={{
      background: isDark ? '#1e293b' : '#ffffff',
      borderRadius: '16px',
      padding: '1.25rem',
      boxShadow: isDark
        ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)'
        : '0 8px 32px rgba(30,58,138,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        marginBottom: '1.25rem',
        paddingBottom: '0.75rem',
        borderBottom: `2px solid ${color}30`
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 20px ${color}40`
        }}>
          <Icon size={18} style={{ color }} />
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '0.9375rem',
          fontWeight: 900,
          color: isDark ? '#f8fafc' : '#0f172a',
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}>
          {title}
        </h3>
      </div>

      {/* Bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {data.map((item, index) => {
          const percentage = (item.value / max) * 100
          const barColor = item.color || color

          return (
            <div key={index} style={{ position: 'relative' }}>
              {/* Label & Value */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.375rem',
                fontSize: '0.75rem',
                fontWeight: 700
              }}>
                <span style={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontWeight: 600
                }}>
                  {item.label}
                </span>
                <span style={{
                  color: barColor,
                  fontWeight: 900,
                  fontSize: '0.8125rem'
                }}>
                  {fmt(item.value)}
                </span>
              </div>

              {/* Bar Background */}
              <div style={{
                width: '100%',
                height: '28px',
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {/* Bar Fill */}
                <div style={{
                  width: `${percentage}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${barColor}, ${barColor}dd)`,
                  borderRadius: '8px',
                  transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  position: 'relative',
                  boxShadow: `0 0 20px ${barColor}60`,
                  animation: 'slideInRight 0.6s ease-out'
                }}>
                  {/* Shine Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'shimmer 2s infinite'
                  }} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Date Range Display */}
      {dateRange && (
        <div style={{
          marginTop: '1rem',
          paddingTop: '0.75rem',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          fontSize: '0.75rem',
          color: isDark ? '#94a3b8' : '#64748b',
          fontWeight: 600,
          textAlign: 'center'
        }}>
          <Calendar size={12} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
          {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
        </div>
      )}
    </div>
  )
}

// ==================== ENHANCED PILL METRIC ====================
const EnhancedPillMetric: FC<{
  id: string
  icon: any
  label: string
  value: string | number
  color: string
  isEditMode: boolean
  onDragStart: (e: React.DragEvent, id: string) => void
  onDragEnter: (id: string) => void
  onDragEnd: () => void
}> = ({
  id,
  icon: Icon,
  label,
  value,
  color,
  isEditMode,
  onDragStart,
  onDragEnter,
  onDragEnd
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      className={`enhanced-pill-wrapper ${isDragging ? 'dragging' : ''} ${isEditMode ? 'draggable' : ''}`}
      draggable={isEditMode}
      onDragStart={(e) => {
        setIsDragging(true)
        onDragStart(e, id)
      }}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      onDragEnter={() => onDragEnter(id)}
      onDragOver={(e) => e.preventDefault()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: isHovered && !isDragging ? 'translateY(-6px) scale(1.03)' : 'translateY(0) scale(1)',
        zIndex: isDragging ? 100 : 1,
        opacity: isDragging ? 0.85 : 1,
        cursor: isEditMode ? 'grab' : 'default'
      }}
    >
      {/* Drag Handle */}
      {isEditMode && (
        <div style={{
          position: 'absolute',
          top: '-6px',
          right: '-6px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10,
          cursor: 'grab',
          border: `2px solid ${color}`
        }}>
          <GripVertical size={12} style={{ color }} />
        </div>
      )}

      {/* Enhanced Pill dengan Multi-Layer Glow */}
      <div style={{
        padding: '0.75rem 1rem',
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        border: `2px solid ${color}`,
        borderRadius: '50px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: `
          0 4px 20px ${color}99,
          0 0 40px ${color}40,
          inset 0 1px 0 rgba(255,255,255,0.3),
          0 4px 8px rgba(0,0,0,0.2)
        `,
        position: 'relative',
        overflow: 'hidden',
        minHeight: '72px',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}>
        {/* Animated Glow Background */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle, ${color}60 0%, transparent 70%)`,
          opacity: isHovered ? 0.9 : 0.5,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
          animation: 'glowPulse 3s ease-in-out infinite'
        }}></div>

        {/* Icon Circle dengan Enhanced Glow */}
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 24px ${color}cc, inset 0 1px 0 rgba(255,255,255,0.4)`,
          position: 'relative',
          zIndex: 1,
          border: `1px solid rgba(255,255,255,0.3)`,
          transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
          transition: 'transform 0.3s ease'
        }}>
          <Icon size={20} style={{
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            position: 'relative',
            zIndex: 1
          }} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
          <div style={{
            fontSize: '0.625rem',
            fontWeight: 800,
            color: 'rgba(255,255,255,0.98)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.125rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}>
            {label}
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 900,
            color: '#ffffff',
            lineHeight: 1,
            textShadow: '0 2px 8px rgba(0,0,0,0.4), 0 0 20px rgba(255,255,255,0.3)'
          }}>
            {typeof value === 'number' ? fmt(value) : value}
          </div>
        </div>

        {/* Live Indicator Dot dengan Glow */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '12px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#ffffff',
          animation: 'livePulse 2s ease-in-out infinite',
          boxShadow: '0 0 12px #ffffff, 0 0 24px #ffffff',
          zIndex: 2
        }}></div>

        {/* Outer Glow Ring */}
        <div style={{
          position: 'absolute',
          inset: '-3px',
          borderRadius: '53px',
          border: `2px solid ${color}`,
          opacity: isHovered ? 0.7 : 0.4,
          pointerEvents: 'none',
          animation: 'outerGlow 3s ease-in-out infinite',
          zIndex: 0
        }}></div>
      </div>

      {/* Drag Overlay */}
      {isDragging && (
        <div style={{
          position: 'absolute',
          inset: '-4px',
          borderRadius: '54px',
          border: '3px dashed #ffffff',
          background: 'rgba(255,255,255,0.3)',
          pointerEvents: 'none',
          animation: 'pulse 0.8s ease-in-out infinite',
          zIndex: 5
        }}></div>
      )}
    </div>
  )
}

// ===== MAIN COMPONENT =====
const WallboardPage = () => {
  const { mode } = useThemeMode()
  const isDark = mode === 'dark'
  const [isEditMode, setIsEditMode] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [wallboardData, setWallboardData] = useState<any>({})
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting')
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const [mounted, setMounted] = useState(false)
  const childRef = useRef<{ cancelOrder: () => void; saveCurrentOrder: () => void }>(null)
  const connectionRef = useRef<{ type: 'sse' | 'ws' | 'poll' | 'idle'; ok: boolean }>({ type: 'idle', ok: false })
  const esRef = useRef<EventSource | null>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectDelay = useRef(1000)

  // ===== DATE RANGE STATE =====
  const [globalDateRange, setGlobalDateRange] = useState<[Moment, Moment]>([moment(), moment()])
  const [topAgentDateRange, setTopAgentDateRange] = useState<[Moment, Moment]>([moment(), moment()])
  const [topTicketDateRange, setTopTicketDateRange] = useState<[Moment, Moment]>([moment(), moment()])

  // ===== DRAG & DROP STATE - UPDATED WITH TICKET METRICS =====
  const [metricsOrder, setMetricsOrder] = useState([
    { id: 'inbound', label: 'Inbound', valueKey: 'inbound_call', icon: Phone, color: '#2563eb' },
    { id: 'answered', label: 'Answered', valueKey: 'inbound_answer', icon: CheckCircle, color: '#059669' },
    { id: 'noanswer', label: 'No Answer', valueKey: 'inbound_no_answer', icon: PhoneMissed, color: '#dc2626' },
    { id: 'inbound_avg_ms', label: 'Avg Wait', valueKey: 'inbound_avg_ms', icon: Timer, color: '#0891b2' },
    { id: 'sla', label: 'SLA', valueKey: 'answer_percent', icon: Target, color: '#d97706', isPercent: true },
    { id: 'abandon', label: 'Abandon', valueKey: 'abandoned_rate', icon: AlertCircle, color: '#dc2626', isPercent: true },
    
    // ✅ NEW TICKET METRICS
    { id: 'totalticket', label: 'Total Ticket', valueKey: 'ticket_total', icon: Ticket, color: '#8b5cf6' },
    { id: 'openticket', label: 'Open Ticket', valueKey: 'ticket_open', icon: FolderOpen, color: '#f59e0b' },
    { id: 'closeticket', label: 'Close Ticket', valueKey: 'ticket_close', icon: CheckSquare, color: '#10b981' },
    { id: 'assignticket', label: 'Assign Ticket', valueKey: 'ticket_assign', icon: UserPlus, color: '#06b6d4' },
    { id: 'eskalasiticket', label: 'Eskalasi', valueKey: 'ticket_eskalasi', icon: AlertTriangle, color: '#ef4444' },
    
    { id: 'outgoing', label: 'Outgoing', valueKey: 'outgoing_calls', icon: Phone, color: '#7c3aed' },
    { id: 'agents', label: 'Agents', valueKey: 'agent_total', icon: Users, color: '#06b6d4' },
    { id: 'ready', label: 'Ready', valueKey: 'agent_ready', icon: Headphones, color: '#059669' },
    { id: 'queue_waiting', label: 'Queue', valueKey: 'queue_waiting', icon: Clock, color: '#d97706' },
    { id: 'busy', label: 'Busy', valueKey: 'busy', icon: ActivityIcon, color: '#db2777' },
    { id: 'inbound_aht', label: 'AHT', valueKey: 'inbound_aht', icon: BarChart2, color: '#2563eb' },

  ])
  const [originalMetricsOrder, setOriginalMetricsOrder] = useState([...metricsOrder])
  const dragMetricId = useRef<string | null>(null)
  const dragOverMetricId = useRef<string | null>(null)

  // ===== TOP AGENT & TOP TICKET DATA =====
  const [topAgentData, setTopAgentData] = useState<BarChartItem[]>([])

  const [topTicketData, setTopTicketData] = useState<BarChartItem[]>([])

  // ===== ULTRA VIBRANT COLOR PALETTE =====
  const themeColors = {
    blue: '#2563eb',
    blueDark: '#1d4ed8',
    blueLight: '#3b82f6',
    blueGlow: 'rgba(37, 99, 235, 0.6)',
    green: '#059669',
    greenDark: '#047857',
    greenLight: '#10b981',
    greenGlow: 'rgba(5, 150, 105, 0.6)',
    red: '#dc2626',
    redDark: '#b91c1c',
    redLight: '#ef4444',
    redGlow: 'rgba(220, 38, 38, 0.6)',
    orange: '#d97706',
    orangeDark: '#b45309',
    orangeLight: '#f59e0b',
    orangeGlow: 'rgba(217, 119, 6, 0.6)',
    cyan: '#0891b2',
    cyanDark: '#0e7490',
    cyanLight: '#06b6d4',
    cyanGlow: 'rgba(8, 145, 178, 0.6)',
    purple: '#7c3aed',
    purpleDark: '#6d28d9',
    purpleLight: '#8b5cf6',
    purpleGlow: 'rgba(124, 58, 237, 0.6)',
    pink: '#db2777',
    pinkDark: '#be185d',
    pinkLight: '#ec4899',
    pinkGlow: 'rgba(219, 39, 119, 0.6)',
    bg: isDark ? '#020617' : '#ffffff',
    cardBg: isDark ? '#0f172a' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
    cardShadow: isDark
      ? '0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.1)'
      : '0 8px 32px rgba(30,58,138,0.2), 0 0 0 1px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.8)',
    headerGradient: isDark
      ? 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #0b41b5 100%)'
      : 'linear-gradient(135deg, #0544cb 0%, #0b60ea 50%, #083a76 100%)',
  }

  // API Configuration
  const apiUrl = process.env.REACT_APP_API_URL
  const endpoint = `${apiUrl}/dashboard/getcount`
  const pollInterval = 3000

  const getApiToken = useCallback(() => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v')
      return raw ? JSON.parse(raw).api_token || '' : ''
    } catch { return '' }
  }, [])

  const appendToken = useCallback((url: string) => {
    const token = getApiToken()
    if (!token) return url
    return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
  }, [getApiToken])

  const applyPayload = useCallback((payload: any) => {
    try {
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload
      const normalize = (v: any) => (typeof v === 'string' ? Number(v) : v)

      setWallboardData(prev => {
        const newData = {
          ...prev,
          inbound_call: normalize(parsed.inbound_call ?? prev.inbound_call),
          inbound_answer: normalize(parsed.inbound_answer ?? prev.inbound_answer),
          inbound_no_answer: normalize(parsed.inbound_no_answer ?? prev.inbound_no_answer),
          inbound_avg_ms: parsed.inbound_avg_ms || "00:00",
          inbound_aht_ms: normalize(parsed.inbound_aht_ms ?? prev.inbound_aht_ms),
          inbound_avg_wait: parsed.inbound_avg_wait || "0s", 
          inbound_aht: parsed.inbound_aht || "00:00",
          answer_percent: normalize(parsed.answer_percent ?? prev.answer_percent),
          no_answer_percent: normalize(parsed.no_answer_percent ?? prev.no_answer_percent),
          abandoned_rate: normalize(parsed.abandoned_rate ?? prev.abandoned_rate),
          ab_pctEarly: normalize(parsed.ab_pctEarly ?? prev.ab_pctEarly),
          ab_pctAgent: normalize(parsed.ab_pctAgent ?? prev.ab_pctAgent),
          ab_pctIvr: normalize(parsed.ab_pctIvr ?? prev.ab_pctIvr),
          online: normalize(parsed.online ?? prev.online),
          agent_ready: normalize(parsed.agent_ready ?? prev.agent_ready),
          agent_aux: normalize(parsed.agent_aux ?? prev.agent_aux),
          active_agents: normalize(parsed.active_agents ?? prev.active_agents),
          agent_total: normalize(parsed.agent_total ?? prev.agent_total),
          registered_ext: normalize(parsed.registered_ext ?? prev.registered_ext),
          waiting_calls: normalize(parsed.waiting_calls ?? prev.waiting_calls),
          queue_waiting: normalize(parsed.queue_waiting ?? prev.queue_waiting),
          outgoing_calls: normalize(parsed.outgoing_calls ?? prev.outgoing_calls),
          call_attempt: normalize(parsed.call_attempt ?? prev.call_attempt),
          call_duration: normalize(parsed.call_duration ?? prev.call_duration),
          busy: normalize(parsed.busy ?? prev.busy),
          failed: normalize(parsed.failed ?? prev.failed),
          csat_very_satisfied: normalize(parsed.csat_very_satisfied ?? prev.csat_very_satisfied),
          csat_satisfied: normalize(parsed.csat_satisfied ?? prev.csat_satisfied),
          csat_neutral: normalize(parsed.csat_neutral ?? prev.csat_neutral),
          csat_dissatisfied: normalize(parsed.csat_dissatisfied ?? prev.csat_dissatisfied),
          csat_very_dissatisfied: normalize(parsed.csat_very_dissatisfied ?? prev.csat_very_dissatisfied),

          // ✅ NEW TICKET FIELDS
          ticket_total: normalize(parsed.ticket_total ?? prev.ticket_total ?? 0),
          ticket_open: normalize(parsed.ticket_open ?? prev.ticket_open ?? 0),
          ticket_close: normalize(parsed.ticket_close ?? prev.ticket_close ?? 0),
          ticket_assign: normalize(parsed.ticket_assign ?? prev.ticket_assign ?? 0),
          ticket_eskalasi: normalize(parsed.ticket_eskalasi ?? prev.ticket_eskalasi ?? 0),
        }
        setLastUpdate(new Date())
        setConnectionStatus('connected')
        setTopAgentData(parsed.perform_agents)
        setTopTicketData(parsed.ticket_categories)
        return newData
      })
    } catch (err) {
      console.error('Parse error:', err)
      setConnectionStatus('disconnected')
    }
  }, [])

  const cleanup = useCallback(() => {
    esRef.current?.close()
    if (pollRef.current) clearInterval(pollRef.current)
    esRef.current = null
    pollRef.current = null
  }, [])

  const startPolling = useCallback(() => {
    connectionRef.current = { type: 'poll', ok: false }
    const run = async () => {
      try {
        const res = await fetch(appendToken(endpoint), {
          headers: { Authorization: `Bearer ${getApiToken()}` }
        })
        const json = await res.json()
        if (json.success) {
          applyPayload(json.data)
          connectionRef.current.ok = true
          setConnectionStatus('connected')
        }
      } catch (err) {
        connectionRef.current.ok = false
        setConnectionStatus('disconnected')
      }
    }
    run()
    pollRef.current = setInterval(run, pollInterval)
  }, [appendToken, endpoint, getApiToken, applyPayload, pollInterval])

  const connect = useCallback(() => {
    cleanup()
    const sseUrl = appendToken(endpoint)
    setConnectionStatus('connecting')
    if (typeof EventSource !== 'undefined') {
      try {
        const es = new EventSource(sseUrl)
        esRef.current = es
        connectionRef.current = { type: 'sse', ok: false }
        es.onopen = () => {
          connectionRef.current.ok = true
          setConnectionStatus('connected')
        }
        es.onmessage = (e) => applyPayload(e.data)
        es.onerror = () => {
          connectionRef.current.ok = false
          setConnectionStatus('disconnected')
          cleanup()
          startPolling()
        }
        return
      } catch (e) {
        console.warn('SSE failed, falling back to polling', e)
      }
    }
    startPolling()
  }, [appendToken, endpoint, cleanup, startPolling, applyPayload])

  useEffect(() => {
    setMounted(true)
    if (!autoRefresh) return

    connect()

    const healthCheck = setInterval(() => {
      if (!connectionRef.current.ok && autoRefresh) {
        reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 30000)
        setTimeout(connect, reconnectDelay.current)
      } else {
        reconnectDelay.current = 1000
      }
    }, 5000)

    return () => {
      cleanup()
      clearInterval(healthCheck)
    }
  }, [connect, cleanup, autoRefresh])

  // ===== DRAG & DROP HANDLERS =====
  const handleMetricDragStart = (e: React.DragEvent, id: string) => {
    dragMetricId.current = id
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleMetricDragEnter = (id: string) => {
    dragOverMetricId.current = id
  }

  const handleMetricDragEnd = () => {
    if (dragMetricId.current && dragOverMetricId.current && dragMetricId.current !== dragOverMetricId.current) {
      const newOrder = [...metricsOrder]
      const draggedIndex = newOrder.findIndex(m => m.id === dragMetricId.current)
      const targetIndex = newOrder.findIndex(m => m.id === dragOverMetricId.current)

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [dragged] = newOrder.splice(draggedIndex, 1)
        newOrder.splice(targetIndex, 0, dragged)
        setMetricsOrder(newOrder)
      }
    }
    dragMetricId.current = null
    dragOverMetricId.current = null
  }

  const handleCancelEdit = () => {
    setMetricsOrder([...originalMetricsOrder])
    childRef.current?.cancelOrder()
    setIsEditMode(false)
  }

  const handleSaveEdit = () => {
    setOriginalMetricsOrder([...metricsOrder])
    childRef.current?.saveCurrentOrder()
    setIsEditMode(false)
  }

  const handleRefresh = () => {
    connect()
  }

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
    return seconds < 10 ? 'Just now'
      : seconds < 60 ? `${seconds}s ago`
        : seconds < 3600 ? `${Math.floor(seconds / 60)}m ago`
          : `${Math.floor(seconds / 3600)}h ago`
  }

  const formatDuration = (ms: number) => {
    if (!ms || ms === 0) return '00:00'
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  // ===== DATE RANGE HANDLERS =====
  const handleGlobalDateChange = (event: any, picker: any) => {
    setGlobalDateRange([picker.startDate, picker.endDate])
  }

  const handleTopAgentDateChange = (event: any, picker: any) => {
    setTopAgentDateRange([picker.startDate, picker.endDate])
  }

  const handleTopTicketDateChange = (event: any, picker: any) => {
    setTopTicketDateRange([picker.startDate, picker.endDate])
  }

  const satisfactionData = [
    { name: "Sangat Puas", value: wallboardData.csat_very_satisfied || 0, color: themeColors.green },
    { name: "Puas", value: wallboardData.csat_satisfied || 0, color: themeColors.green },
    { name: "Cukup Puas", value: wallboardData.csat_neutral || 0, color: themeColors.orange },
    { name: "Tidak Puas", value: wallboardData.csat_dissatisfied || 0, color: themeColors.red },
    { name: "Sangat Tidak Puas", value: wallboardData.csat_very_dissatisfied || 0, color: themeColors.red },

  ]

  const abandonData = [
    { name: "Early", value: wallboardData.ab_pctEarly || 0, color: themeColors.green },
    { name: "Agent", value: wallboardData.ab_pctAgent || 0, color: themeColors.orange },
    { name: "IVR", value: wallboardData.ab_pctIvr || 0, color: themeColors.red },
  ]

  const safeData = {
    inbound_call: wallboardData.inbound_call || 0,
    inbound_answer: wallboardData.inbound_answer || 0,
    inbound_no_answer: wallboardData.inbound_no_answer || 0,
    inbound_avg_ms: wallboardData.inbound_avg_ms || 0,
    answer_percent: wallboardData.answer_percent || 0,
    no_answer_percent: wallboardData.no_answer_percent || 0,
    abandoned_rate: wallboardData.abandoned_rate || 0,
    ab_pctEarly: wallboardData.ab_pctEarly || 0,
    ab_pctAgent: wallboardData.ab_pctAgent || 0,
    ab_pctIvr: wallboardData.ab_pctIvr || 0,
    online: wallboardData.online || 0,
    agent_ready: wallboardData.agent_ready || 0,
    agent_aux: wallboardData.agent_aux || 0,
    active_agents: wallboardData.active_agents || 0,
    agent_total: wallboardData.agent_total || 0,
    registered_ext: wallboardData.registered_ext || 0,
    waiting_calls: wallboardData.waiting_calls || 0,
    queue_waiting: wallboardData.queue_waiting || 0,
    outgoing_calls: wallboardData.outgoing_calls || 0,
    call_attempt: wallboardData.call_attempt || 0,
    call_duration: formatDuration(wallboardData.call_duration || 0),
    busy: wallboardData.busy || 0,
    failed: wallboardData.failed || 0,
    inbound_aht: wallboardData.inbound_aht || 0,
    // ✅ NEW TICKET DATA
    ticket_total: wallboardData.ticket_total || 0,
    ticket_open: wallboardData.ticket_open || 0,
    ticket_close: wallboardData.ticket_close || 0,
    ticket_assign: wallboardData.ticket_assign || 0,
    ticket_eskalasi: wallboardData.ticket_eskalasi || 0,
  }

  return (
    <div className='ultra-vibrant-wallboard-container' style={{
      background: isDark
        ? 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #020617 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 50%, #ffffff 100%)',
      minHeight: '100vh',
      padding: '1rem 1.5rem',
      fontFamily: "'Inter', system-ui, sans-serif"
    }}>
      {/* ===== ENHANCED HEADER dengan DATE RANGE PICKER ===== */}
      <div className='ultra-vibrant-header' style={{
        background: themeColors.headerGradient,
        borderRadius: '20px',
        padding: '1.25rem 2rem',
        marginBottom: '1.25rem',
        boxShadow: `0 8px 32px ${isDark ? 'rgba(37,99,235,0.5)' : 'rgba(37,99,235,0.3)'}, inset 0 1px 0 rgba(255,255,255,0.2)`,
        position: 'relative',
        overflow: 'hidden',
        animation: mounted ? 'slideInDown 0.6s ease-out' : 'none',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)'}`
      }}>
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(139,92,246,0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Top Row: Title & Connection Status */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '16px',
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(12px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.4)',
                animation: 'pulse 2s ease-in-out infinite',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                <Activity size={28} style={{ color: '#fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.5px', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  LIVE WALLBOARD
                </h1>
                <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.875rem', color: 'rgba(255,255,255,0.95)', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
                  Real-time Monitoring Dashboard
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
              <div style={{
                padding: '0.625rem 1.25rem',
                borderRadius: '50px',
                background: connectionStatus === 'connected' ? 'rgba(5,150,105,0.35)' :
                  connectionStatus === 'connecting' ? 'rgba(217,119,6,0.35)' : 'rgba(220,38,38,0.35)',
                color: connectionStatus === 'connected' ? themeColors.green :
                  connectionStatus === 'connecting' ? themeColors.orange : themeColors.red,
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                fontWeight: 800,
                border: `1px solid ${connectionStatus === 'connected' ? themeColors.green :
                  connectionStatus === 'connecting' ? themeColors.orange : themeColors.red}`,
                fontSize: '0.8125rem',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
              }}>
                <div style={{
                  width: '9px', height: '9px', borderRadius: '50%',
                  background: 'currentColor',
                  animation: 'pulse 2s ease-in-out infinite',
                  boxShadow: '0 0 12px currentColor'
                }}></div>
                <span>{connectionStatus === 'connected' ? '● LIVE' : connectionStatus}</span>
              </div>

              <button onClick={handleRefresh} disabled={false} className='refresh-button-premium' style={{
                padding: '0.625rem 1.25rem',
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '50px',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                fontWeight: 700,
                fontSize: '0.8125rem',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
              }}>
                <RefreshCw size={16} className='' />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Bottom Row: Date Range Pickers */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '1rem',
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.3)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`
          }}>
            {/* Global Date Range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} style={{ color: '#fff' }} />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.8125rem' }}>Global:</span>
              {/* @ts-ignore */}
              <DateRangePicker
                initialSettings={{
                  startDate: globalDateRange[0],
                  endDate: globalDateRange[1],
                  ranges: {
                    'Today': [moment(), moment()],
                    'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                    'This Month': [moment().startOf('month'), moment().endOf('month')],
                    'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                  }
                }}
                onApply={handleGlobalDateChange}
              >
                <button style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}>
                  {globalDateRange[0].format('DD MMM')} - {globalDateRange[1].format('DD MMM YYYY')}
                </button>
              </DateRangePicker>
            </div>

            <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.3)' }}></div>

            {/* Top Agent Date Range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Award size={16} style={{ color: themeColors.cyan }} />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.8125rem' }}>Top Agent:</span>
              {/* @ts-ignore */}
              <DateRangePicker
                initialSettings={{
                  startDate: topAgentDateRange[0],
                  endDate: topAgentDateRange[1],
                  ranges: {
                    'Today': [moment(), moment()],
                    'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                    'This Month': [moment().startOf('month'), moment().endOf('month')],
                    'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                  }
                }}
                onApply={handleTopAgentDateChange}
              >
                <button style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}>
                  {topAgentDateRange[0].format('DD MMM')} - {topAgentDateRange[1].format('DD MMM YYYY')}
                </button>
              </DateRangePicker>
            </div>

            <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.3)' }}></div>

            {/* Top Ticket Date Range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Ticket size={16} style={{ color: themeColors.green }} />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.8125rem' }}>Top Ticket:</span>
              {/* @ts-ignore */}
              <DateRangePicker
                initialSettings={{
                  startDate: topTicketDateRange[0],
                  endDate: topTicketDateRange[1],
                  ranges: {
                    'Today': [moment(), moment()],
                    'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                    'This Month': [moment().startOf('month'), moment().endOf('month')],
                    'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                  }
                }}
                onApply={handleTopTicketDateChange}
              >
                <button style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}>
                  {topTicketDateRange[0].format('DD MMM')} - {topTicketDateRange[1].format('DD MMM YYYY')}
                </button>
              </DateRangePicker>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MAIN GRID - ENHANCED LAYOUT ===== */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '1rem',
        animation: mounted ? 'fadeInUp 0.7s ease-out 0.2s both' : 'none'
      }}>
        {/* Service Level - Enhanced Dual Gauge */}
        <div className='metric-card-hover' style={{
          gridColumn: 'span 4',
          background: themeColors.cardBg,
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: themeColors.cardShadow,
          border: `1px solid ${themeColors.border}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(90deg, ${themeColors.green}, ${themeColors.red})`
          }}></div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            marginBottom: '1.25rem', justifyContent: 'center',
            position: 'relative', zIndex: 1
          }}>
            <Target size={20} style={{ color: themeColors.blue }} />
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 900, color: themeColors.text, letterSpacing: '0.5px' }}>
              SERVICE LEVEL
            </h3>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '2.5rem', position: 'relative', zIndex: 1 }}>
            <div className='gauge-hover-item' style={{ textAlign: 'center' }}>
              <ServiceLevelInbound
                value={safeData.answer_percent}
                label=""
                color={themeColors.green}
                size={100}
                isDark={isDark}
              />
              <div style={{ marginTop: '0.625rem', fontSize: '1.25rem', fontWeight: 900, color: themeColors.green, textShadow: `0 0 20px ${themeColors.greenGlow}` }}>
                {fmt(safeData.inbound_answer)}
              </div>
              <div style={{ fontSize: '0.6875rem', color: themeColors.textMuted, fontWeight: 700 }}>ANSWERED</div>
            </div>

            <div className='gauge-hover-item' style={{ textAlign: 'center' }}>
              <ServiceLevelInbound
                value={safeData.no_answer_percent}
                label=""
                color={themeColors.red}
                size={100}
                isDark={isDark}
              />
              <div style={{ marginTop: '0.625rem', fontSize: '1.25rem', fontWeight: 900, color: themeColors.red, textShadow: `0 0 20px ${themeColors.redGlow}` }}>
                {fmt(safeData.inbound_no_answer)}
              </div>
              <div style={{ fontSize: '0.6875rem', color: themeColors.textMuted, fontWeight: 700 }}>ABANDONED</div>
            </div>
          </div>
        </div>

        {/* Customer Satisfaction - Enhanced */}
        <div className='metric-card-hover' style={{
          gridColumn: 'span 4',
          background: themeColors.cardBg,
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: themeColors.cardShadow,
          border: `1px solid ${themeColors.border}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(90deg, ${themeColors.green}, ${themeColors.orange}, ${themeColors.red})`
          }}></div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            marginBottom: '1rem', justifyContent: 'center',
            position: 'relative', zIndex: 1
          }}>
            <Star size={18} style={{ color: themeColors.orange }} />
            <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 900, color: themeColors.text }}>
              SATISFACTION
            </h3>
          </div>

          <SatisfactionBarChart 
            data={satisfactionData} 
            isDark={isDark} 
          />

        </div>

        {/* Abandon Call - Enhanced */}
        <div className='metric-card-hover' style={{
          gridColumn: 'span 4',
          background: themeColors.cardBg,
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: themeColors.cardShadow,
          border: `1px solid ${themeColors.border}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(90deg, ${themeColors.green}, ${themeColors.orange}, ${themeColors.red})`
          }}></div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            marginBottom: '1rem', justifyContent: 'center',
            position: 'relative', zIndex: 1
          }}>
            <PhoneMissed size={20} style={{ color: themeColors.red }} />
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 900, color: themeColors.text, letterSpacing: '0.5px' }}>
              ABANDON
            </h3>
          </div>

          <div className="text-center" style={{ position: 'relative', zIndex: 1 }}>
            <AnalyticsPieCard
              data={abandonData}
              size={130}
              showLegend={false}
              isDark={isDark}
            />
          </div>

          <div style={{
            display: 'flex', justifyContent: 'center', gap: '1rem',
            marginTop: '0.75rem', fontSize: '0.75rem', fontWeight: 800,
            position: 'relative', zIndex: 1
          }}>
            <span className='legend-badge' style={{ color: themeColors.green }}>● Early</span>
            <span className='legend-badge' style={{ color: themeColors.orange }}>● Agent</span>
            <span className='legend-badge' style={{ color: themeColors.red }}>● IVR</span>
          </div>
        </div>

        {/* ===== TOP AGENT & TOP TICKET CHARTS dengan Date Range ===== */}
        <div style={{
          gridColumn: 'span 6',
          animation: `fadeInUp 0.7s ease-out 0.3s both`
        }}>
          <HorizontalBarChart
            data={topAgentData}
            title="TOP AGENT"
            icon={Award}
            color={themeColors.cyan}
            isDark={isDark}
            dateRange={{
              start: topAgentDateRange[0].toDate(),
              end: topAgentDateRange[1].toDate()
            }}
          />
        </div>

        <div style={{
          gridColumn: 'span 6',
          animation: `fadeInUp 0.7s ease-out 0.4s both`
        }}>
          <HorizontalBarChart
            data={topTicketData}
            title="TOP TICKET"
            icon={Ticket}
            color={themeColors.green}
            isDark={isDark}
            dateRange={{
              start: topTicketDateRange[0].toDate(),
              end: topTicketDateRange[1].toDate()
            }}
          />
        </div>

        {/* ===== ENHANCED METRICS PILLS - ROW 1 (6 items) ===== */}
        <div style={{
          gridColumn: 'span 12',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '0.875rem'
        }}>
          {metricsOrder.slice(0, 6).map((metric, index) => (
            <div key={metric.id} style={{ animation: `fadeInUp 0.5s ease-out ${0.25 + index * 0.05}s both` }}>
              <EnhancedPillMetric
                id={metric.id}
                icon={metric.icon}
                label={metric.label}
                value={metric.isPercent
                  ? `${safeData[metric.valueKey as keyof typeof safeData]}%`
                  : safeData[metric.valueKey as keyof typeof safeData]
                }
                color={metric.color}
                isEditMode={isEditMode}
                onDragStart={handleMetricDragStart}
                onDragEnter={handleMetricDragEnter}
                onDragEnd={handleMetricDragEnd}
              />
            </div>
          ))}
        </div>

        {/* ===== ENHANCED METRICS PILLS - ROW 2 (6 items including TICKETS) ===== */}
        <div style={{
          gridColumn: 'span 12',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '0.875rem'
        }}>
          {metricsOrder.slice(6, 12).map((metric, index) => (
            <div key={metric.id} style={{ animation: `fadeInUp 0.5s ease-out ${0.45 + index * 0.05}s both` }}>
              <EnhancedPillMetric
                id={metric.id}
                icon={metric.icon}
                label={metric.label}
                value={metric.isPercent
                  ? `${safeData[metric.valueKey as keyof typeof safeData]}%`
                  : safeData[metric.valueKey as keyof typeof safeData]
                }
                color={metric.color}
                isEditMode={isEditMode}
                onDragStart={handleMetricDragStart}
                onDragEnter={handleMetricDragEnter}
                onDragEnd={handleMetricDragEnd}
              />
            </div>
          ))}
        </div>

        {/* ===== ENHANCED METRICS PILLS - ROW 3 (TICKET METRICS - 5 items) ===== */}
        <div style={{
          gridColumn: 'span 12',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '0.875rem',
          padding: '0.5rem',
          background: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
          borderRadius: '16px',
          border: `1px solid ${isDark ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)'}`
        }}>
          {metricsOrder.slice(12, 17).map((metric, index) => (
            <div key={metric.id} style={{ animation: `fadeInUp 0.5s ease-out ${0.55 + index * 0.05}s both` }}>
              <EnhancedPillMetric
                id={metric.id}
                icon={metric.icon}
                label={metric.label}
                value={safeData[metric.valueKey as keyof typeof safeData]}
                color={metric.color}
                isEditMode={isEditMode}
                onDragStart={handleMetricDragStart}
                onDragEnter={handleMetricDragEnter}
                onDragEnd={handleMetricDragEnd}
              />
            </div>
          ))}
        </div>

        {/* Real-time Metrics - Enhanced Full Width */}
        <div className='metric-card-hover' style={{
          gridColumn: 'span 12',
          background: themeColors.cardBg,
          borderRadius: '20px',
          padding: '1.25rem',
          boxShadow: themeColors.cardShadow,
          border: `1px solid ${themeColors.border}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(90deg, ${themeColors.orange}, ${themeColors.green})`
          }}></div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            marginBottom: '1.25rem', justifyContent: 'center',
            position: 'relative', zIndex: 1
          }}>
            <Zap size={20} style={{ color: themeColors.orange, filter: `drop-shadow(0 0 8px ${themeColors.orangeGlow})` }} />
            <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 900, color: themeColors.text, letterSpacing: '0.5px' }}>
              REAL-TIME METRICS
            </h3>
            <span style={{
              padding: '0.375rem 0.875rem',
              background: themeColors.green,
              color: '#ffffff',
              borderRadius: '50px',
              fontSize: '0.75rem',
              fontWeight: 900,
              animation: 'pulse 2s ease-in-out infinite',
              boxShadow: `0 0 16px ${themeColors.greenGlow}`,
              border: `1px solid rgba(255,255,255,0.3)`
            }}>● LIVE</span>
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <RealtimeCounts
              ref={childRef}
              filter="live"
              isEditMode={isEditMode}
              isDark={isDark}
              themeColors={themeColors}
              compact={true}
            />
          </div>
        </div>
      </div>

      {/* ===== ENHANCED FOOTER ===== */}
      <div className='footer-hover' style={{
        marginTop: '1.25rem',
        padding: '1rem 1.5rem',
        background: themeColors.cardBg,
        borderRadius: '16px',
        border: `1px solid ${themeColors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.8125rem',
        color: themeColors.textMuted,
        boxShadow: themeColors.cardShadow,
        animation: mounted ? 'fadeInUp 0.7s ease-out 0.6s both' : 'none',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', fontWeight: 700 }}>
          <span>Auto: <b style={{ color: autoRefresh ? themeColors.green : themeColors.textMuted, textShadow: autoRefresh ? `0 0 12px ${themeColors.greenGlow}` : 'none' }}>
            {autoRefresh ? 'ON' : 'OFF'}
          </b></span>
          <span style={{ opacity: 0.4 }}>|</span>
          <span>Edit: <b style={{ color: isEditMode ? themeColors.orange : themeColors.textMuted, textShadow: isEditMode ? `0 0 12px ${themeColors.orangeGlow}` : 'none' }}>
            {isEditMode ? 'ON' : 'OFF'}
          </b></span>
          <span style={{ opacity: 0.4 }}>|</span>
          <span>Sync: <b style={{ color: themeColors.text, textShadow: '0 0 12px rgba(255,255,255,0.3)' }}>{formatTimeAgo(lastUpdate)}</b></span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          {isEditMode ? (
            <>
              <button onClick={handleCancelEdit} className='btn-premium btn-danger' style={{
                padding: '0.625rem 1.25rem',
                background: themeColors.red,
                color: '#fff',
                border: 'none',
                borderRadius: '50px',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: '0.8125rem',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: `0 4px 16px ${themeColors.redGlow}`
              }}>CANCEL</button>

              <button onClick={handleSaveEdit} className='btn-premium btn-success' style={{
                padding: '0.625rem 1.25rem',
                background: themeColors.green,
                color: '#fff',
                border: 'none',
                borderRadius: '50px',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: '0.8125rem',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: `0 4px 16px ${themeColors.greenGlow}`
              }}>SAVE</button>
            </>
          ) : (
            <button onClick={() => setIsEditMode(true)} className='btn-premium btn-primary' style={{
              padding: '0.625rem 1.25rem',
              background: themeColors.blue,
              color: '#fff',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer',
              fontWeight: 800,
              fontSize: '0.8125rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: `0 4px 16px ${themeColors.blueGlow}`
            }}>
              <Move size={16} /> CUSTOMIZE
            </button>
          )}
        </div>
      </div>

      <div style={{
        textAlign: 'center',
        marginTop: '1rem',
        fontSize: '0.75rem',
        color: themeColors.textMuted,
        opacity: 0.7,
        fontWeight: 700
      }}>
        2026©SmartCall Apps • Ultra Vibrant Wallboard
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.05); }
        }
        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.9; }
        }
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 12px #ffffff, 0 0 24px #ffffff; }
          50% { opacity: 0.7; transform: scale(1.2); box-shadow: 0 0 16px #ffffff, 0 0 32px #ffffff; }
        }
        @keyframes outerGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .enhanced-pill-wrapper {
          will-change: transform;
        }
        .enhanced-pill-wrapper.dragging {
          cursor: grabbing !important;
          z-index: 1000 !important;
        }
        .enhanced-pill-wrapper.draggable:hover {
          cursor: grab;
        }
        .enhanced-pill-wrapper.draggable:active {
          cursor: grabbing;
        }
        
        /* ===== HOVER EFFECTS VIA CSS CLASSES ===== */
        .metric-card-hover {
          transition: all 0.3s ease;
        }
        .metric-card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3) !important;
        }
        .gauge-hover-item {
          transition: transform 0.3s ease;
        }
        .gauge-hover-item:hover {
          transform: scale(1.05);
        }
        .legend-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          border: 1px solid currentColor;
          transition: all 0.2s ease;
        }
        .legend-badge:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px currentColor;
        }
        .refresh-button-premium:hover:not(:disabled) {
          background: rgba(255,255,255,0.35) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(0,0,0,0.3) !important;
        }
        .btn-premium {
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.3) !important;
          filter: brightness(1.1);
        }
        .btn-danger:hover {
          box-shadow: 0 6px 20px rgba(220, 38, 38, 0.6) !important;
        }
        .btn-success:hover {
          box-shadow: 0 6px 20px rgba(5, 150, 105, 0.6) !important;
        }
        .btn-primary:hover {
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.6) !important;
        }
        .footer-hover {
          transition: all 0.3s ease;
        }
        .footer-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
        }
        
        @media (max-width: 1920px) {
          .ultra-vibrant-wallboard-container {
            padding: 0.875rem 1.25rem;
          }
        }
        @media (max-width: 1366px) {
          [style*="gridTemplateColumns: 'repeat(6, 1fr)'"] {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          [style*="gridTemplateColumns: 'repeat(5, 1fr)'"] {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 1024px) {
          [style*="gridTemplateColumns: 'repeat(6, 1fr)'"] {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          [style*="gridTemplateColumns: 'repeat(5, 1fr)'"] {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          [style*="gridColumn: 'span 4'"] {
            grid-column: span 6 !important;
          }
          [style*="gridColumn: 'span 6'"] {
            grid-column: span 12 !important;
          }
        }
        @media (max-width: 768px) {
          [style*="gridTemplateColumns: 'repeat(6, 1fr)'"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          [style*="gridTemplateColumns: 'repeat(5, 1fr)'"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          [style*="gridColumn: 'span 4'"] {
            grid-column: span 12 !important;
          }
          .ultra-vibrant-header {
            padding: 1rem 1.25rem;
          }
          .ultra-vibrant-header h1 {
            font-size: 1.5rem !important;
          }
        }
        @media (max-width: 480px) {
          [style*="gridTemplateColumns: 'repeat(6, 1fr)'"] {
            grid-template-columns: 1fr 1fr !important;
          }
          [style*="gridTemplateColumns: 'repeat(5, 1fr)'"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  )
}

export default WallboardPage